import { Component, HostListener, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';

import {
  LegalDocument,
  LegalDocumentLimits,
  LegalSource,
  PlatformLegalDocumentsService,
} from '../../services/platform-legal-documents.service';
import { NotificationService } from '../../services/notification.service';
import { DialogService } from '../../services/dialog.service';
import { friendlyError } from '../../utils/friendly-error';
import { formatLongDate } from '../../utils/format';
import { RichTextEditorComponent } from './rich-text-editor.component';
import { LegalDocumentViewerComponent } from './legal-document-viewer.component';

interface Draft {
  id: number | null;
  title: string;
  source: LegalSource;
  content: string;
  is_published: boolean;
  /** Archivo nuevo elegido (aún sin subir). */
  file: File | null;
  /** El archivo guardado se quitará al guardar. */
  remove_file: boolean;
}

const DEFAULT_LIMITS: LegalDocumentLimits = {
  documents: 12,
  content_max: 300000,
  file_max_bytes: 15 * 1024 * 1024,
  file_extensions: ['pdf', 'png', 'jpg', 'jpeg', 'webp'],
};

const MIME_BY_EXTENSION: Record<string, string> = {
  pdf: 'application/pdf', png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', webp: 'image/webp',
};

const plainText = (html: string): string => {
  const doc = new DOMParser().parseFromString(html || '', 'text/html');
  return (doc.body.textContent || '').replace(/ /g, ' ').trim();
};

/**
 * Documentos legales de TuSala (Centro de operaciones > Personalización >
 * Documentos legales). Cada documento se escribe con el editor de texto o se
 * sube como PDF/imagen; la landing y el front de clientes lo muestran en el
 * mismo modal (el archivo se dibuja en un canvas con pdf.js).
 */
@Component({
  selector: 'app-platform-legal-documents-admin',
  standalone: true,
  imports: [CommonModule, FormsModule, RichTextEditorComponent, LegalDocumentViewerComponent],
  template: `
    <div class="legal-shell" *ngIf="!loading(); else loadingState">
      <div class="legal-layout">
        <!-- Lista de documentos -->
        <aside class="legal-list-card" aria-label="Documentos legales">
          <header class="list-head">
            <span class="count-pill" [class.full]="atLimit()">{{ documents().length }}<small>/{{ limits().documents }}</small></span>
            <button type="button" class="primary-add" (click)="startNew()" [disabled]="atLimit()"><i class="bi bi-plus-lg"></i><span>Nuevo documento</span></button>
          </header>
          <ul class="doc-list" role="listbox" aria-label="Elige un documento para editarlo">
            <li *ngFor="let doc of documents(); trackBy: trackDoc">
              <button type="button" class="doc-item" role="option" [attr.aria-selected]="draft.id === doc.id" [class.active]="draft.id === doc.id" (click)="select(doc)">
                <span class="doc-icon" [ngClass]="iconClass(doc.source, doc.file?.mime)" aria-hidden="true"><i class="bi" [ngClass]="iconFor(doc.source, doc.file?.mime)"></i></span>
                <span class="doc-text">
                  <strong>{{ doc.title }}</strong>
                  <small>v{{ doc.version }} · {{ shortDate(doc.updated_at) }}</small>
                </span>
                <span class="doc-state" [class.on]="doc.is_published">{{ doc.is_published ? 'Publicado' : 'Borrador' }}</span>
              </button>
            </li>
            <li *ngIf="draft.id === null">
              <button type="button" class="doc-item active is-new" role="option" aria-selected="true">
                <span class="doc-icon" aria-hidden="true"><i class="bi bi-file-earmark-plus"></i></span>
                <span class="doc-text"><strong>{{ draft.title || 'Nuevo documento' }}</strong><small>Sin guardar</small></span>
              </button>
            </li>
          </ul>
          <ul class="list-tips">
            <li><i class="bi bi-window-stack"></i><span>Se muestran en un <strong>modal</strong> en la landing y en el sitio de clientes.</span></li>
            <li><i class="bi bi-clock-history"></i><span>Los cambios se reflejan en hasta 1 minuto.</span></li>
          </ul>
        </aside>

        <!-- Edición -->
        <section class="legal-editor-card" aria-label="Editar documento" *ngIf="hasSelection()">
          <div class="editor-body">
            <div class="editor-head">
              <label class="field field-title">
                <span>Título <em class="req">*</em></span>
                <input type="text" maxlength="120" [(ngModel)]="draft.title" (ngModelChange)="touch()" placeholder="Ej. Política de cookies" [class.invalid]="!!titleError()" data-no-limit-hint />
                <small class="field-error" *ngIf="titleError()" role="alert"><i class="bi bi-exclamation-circle"></i> {{ titleError() }}</small>
              </label>
              <div class="meta-chips" *ngIf="current() as doc">
                <span class="chip" title="Clave usada en los enlaces"><i class="bi bi-link-45deg"></i>{{ doc.key }}</span>
                <span class="chip"><i class="bi bi-tag"></i>v{{ doc.version }}</span>
                <span class="chip" *ngIf="doc.is_system" title="Documento base: no se puede eliminar"><i class="bi bi-shield-lock"></i>Base</span>
              </div>
            </div>

            <div class="field">
              <span>¿Cómo quieres cargar el contenido?</span>
              <div class="source-switch" role="radiogroup" aria-label="Origen del contenido">
                <button type="button" role="radio" [attr.aria-checked]="draft.source === 'editor'" [class.on]="draft.source === 'editor'" (click)="setSource('editor')">
                  <i class="bi bi-pencil-square"></i><span><strong>Escribir en el editor</strong><em>Texto con formato, títulos y listas</em></span>
                </button>
                <button type="button" role="radio" [attr.aria-checked]="draft.source === 'file'" [class.on]="draft.source === 'file'" (click)="setSource('file')">
                  <i class="bi bi-file-earmark-arrow-up"></i><span><strong>Subir un archivo</strong><em>PDF o imagen (PNG, JPG, WEBP)</em></span>
                </button>
              </div>
            </div>

            <div class="field" *ngIf="draft.source === 'editor'">
              <span>Contenido <em class="req" *ngIf="draft.is_published">*</em></span>
              <app-rich-text-editor [ngModel]="draft.content" (ngModelChange)="draft.content = $event; touch()" [maxLength]="limits().content_max"
                                    [invalid]="!!contentError()" placeholder="Escribe aquí el documento. Usa «Título» y «Subtítulo» para separar las secciones."></app-rich-text-editor>
              <small class="field-error" *ngIf="contentError()" role="alert"><i class="bi bi-exclamation-circle"></i> {{ contentError() }}</small>
              <small class="field-hint" *ngIf="current()?.file && !contentError()"><i class="bi bi-info-circle"></i> El archivo «{{ current()?.file?.name }}» se conserva; puedes volver a «Subir un archivo» cuando quieras.</small>
            </div>

            <div class="field" *ngIf="draft.source === 'file'">
              <span>Archivo <em class="req" *ngIf="draft.is_published">*</em></span>
              <input #fileInput type="file" class="sr-only" [accept]="acceptAttr()" (change)="onFilePicked($event)" />
              <div class="dropzone" *ngIf="!activeFileInfo(); else fileCard" [class.over]="dragOver()" [class.invalid]="!!fileError()"
                   (click)="fileInput.click()" (keydown.enter)="fileInput.click()" (keydown.space)="$event.preventDefault(); fileInput.click()" tabindex="0" role="button"
                   (dragover)="onDragOver($event)" (dragleave)="dragOver.set(false)" (drop)="onDrop($event)" aria-label="Elegir archivo">
                <span class="drop-icon"><i class="bi bi-cloud-arrow-up"></i></span>
                <strong>Arrastra el archivo aquí o haz clic para elegirlo</strong>
                <small>PDF, PNG, JPG o WEBP · máximo {{ maxMb() }} MB</small>
              </div>
              <ng-template #fileCard>
                <div class="file-card" [class.over]="dragOver()" (dragover)="onDragOver($event)" (dragleave)="dragOver.set(false)" (drop)="onDrop($event)">
                  <span class="doc-icon big" [ngClass]="iconClass('file', activeFileInfo()!.mime)"><i class="bi" [ngClass]="iconFor('file', activeFileInfo()!.mime)"></i></span>
                  <div class="file-text">
                    <strong>{{ activeFileInfo()!.name }}</strong>
                    <small>{{ fileTypeLabel(activeFileInfo()!.mime) }} · {{ formatSize(activeFileInfo()!.size) }} · {{ draft.file ? 'Nuevo, sin guardar' : 'Guardado' }}</small>
                  </div>
                  <div class="file-actions">
                    <button type="button" class="secondary-btn" (click)="fileInput.click()"><i class="bi bi-arrow-repeat"></i> Reemplazar</button>
                    <button type="button" class="icon-action danger" (click)="removeFile()" title="Quitar archivo" aria-label="Quitar archivo"><i class="bi bi-trash3"></i></button>
                  </div>
                </div>
              </ng-template>
              <small class="field-error" *ngIf="fileError()" role="alert"><i class="bi bi-exclamation-circle"></i> {{ fileError() }}</small>
              <small class="field-hint" *ngIf="!fileError()"><i class="bi bi-info-circle"></i> Se mostrará página por página dentro del modal, con zoom. ¿Tienes un Word? Guárdalo como PDF antes de subirlo.</small>
            </div>

            <button type="button" role="switch" class="visibility-switch wide" [class.on]="draft.is_published" [attr.aria-checked]="draft.is_published" (click)="draft.is_published = !draft.is_published; touch()">
              <span class="switch-track"><span class="switch-knob"></span></span>
              <span class="switch-text"><strong>{{ draft.is_published ? 'Publicado' : 'Borrador (no publicado)' }}</strong>
                <em>{{ draft.is_published ? 'Visible en el footer de la landing y del sitio de clientes.' : 'No aparece en ningún frente hasta que lo publiques.' }}</em></span>
            </button>
          </div>

          <footer class="legal-actions" [class.dirty]="dirty()">
            <span class="save-state" [class.pending]="dirty()">
              <i class="bi" [class.bi-check2-circle]="!dirty()" [class.bi-circle-fill]="dirty()" aria-hidden="true"></i>
              {{ dirty() ? 'Tienes cambios sin guardar' : 'Todo está guardado' }}
            </span>
            <div class="action-buttons">
              <button type="button" class="icon-action danger" *ngIf="current() && !current()!.is_system" (click)="remove()" [disabled]="saving()" title="Eliminar documento" aria-label="Eliminar documento"><i class="bi bi-trash3"></i></button>
              <button type="button" class="secondary-btn" (click)="openPreview()" [disabled]="saving()"><i class="bi bi-eye"></i> Vista previa</button>
              <button type="button" class="secondary-btn" (click)="discard()" [disabled]="saving() || !dirty()">Descartar</button>
              <button type="button" class="action-btn" (click)="save()" [disabled]="saving() || !dirty()">
                <i class="bi" [class.bi-cloud-check]="!saving()" [class.bi-arrow-repeat]="saving()" [class.spin]="saving()"></i>{{ saving() ? 'Guardando...' : (draft.id === null ? 'Crear documento' : 'Guardar cambios') }}
              </button>
            </div>
          </footer>
        </section>
      </div>
    </div>

    <!-- Vista previa: el mismo modal que verá el público. -->
    <div *ngIf="previewOpen()" class="modal-overlay legal-preview-overlay" (click)="closePreview()">
      <section class="legal-preview" (click)="$event.stopPropagation()" role="dialog" aria-modal="true" aria-labelledby="legal-preview-title">
        <header class="lp-head">
          <span class="lp-badge" aria-hidden="true"><i class="bi bi-file-earmark-text"></i></span>
          <div class="lp-heading">
            <span class="lp-eyebrow">Documento legal · Vista previa</span>
            <h3 id="legal-preview-title">{{ draft.title || 'Sin título' }}</h3>
            <small>{{ previewMeta() }}</small>
          </div>
          <button type="button" class="lp-close" aria-label="Cerrar vista previa" (click)="closePreview()"><i class="bi bi-x-lg"></i></button>
        </header>
        <app-legal-document-viewer class="lp-viewer"
          [html]="draft.source === 'editor' ? draft.content : ''"
          [fileBlob]="draft.source === 'file' ? previewBlob() : null"
          [mime]="activeFileInfo()?.mime || ''"
          [fileName]="activeFileInfo()?.name || ''"
          [emptyText]="draft.source === 'file' ? 'Aún no hay archivo para mostrar.' : 'Aún no hay contenido para mostrar.'"></app-legal-document-viewer>
        <div class="lp-loading" *ngIf="previewLoading()"><span class="spinner"></span><span>Preparando archivo…</span></div>
      </section>
    </div>

    <ng-template #loadingState>
      <div class="legal-loading" *ngIf="!loadError(); else loadErrorState"><span class="spinner"></span><span>Cargando documentos legales…</span></div>
      <ng-template #loadErrorState>
        <div class="legal-load-error" role="alert">
          <i class="bi bi-wifi-off"></i><span>{{ loadError() }}</span>
          <button type="button" class="secondary-btn" (click)="load()"><i class="bi bi-arrow-clockwise"></i> Reintentar</button>
        </div>
      </ng-template>
    </ng-template>
  `,
  styles: [`
    :host{display:block}
    .sr-only{position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0}
    .legal-shell{container-type:inline-size;container-name:legal-shell}
    .legal-layout{display:grid;grid-template-columns:290px minmax(0,1fr);gap:1.1rem;align-items:start}
    .legal-list-card,.legal-editor-card{border:1px solid var(--ts-border-strong);border-radius:16px;background:linear-gradient(165deg,var(--ts-surface),var(--ts-bg-elevated));box-shadow:var(--ts-shadow-sm)}

    /* ---------- Lista ---------- */
    .legal-list-card{position:sticky;top:16px;display:grid;gap:.85rem;padding:1rem}
    .list-head{display:flex;align-items:center;justify-content:space-between;gap:.6rem}
    .count-pill{display:inline-flex;align-items:baseline;gap:1px;padding:.3rem .65rem;color:var(--ts-text);border:1px solid var(--ts-border-strong);border-radius:999px;font-size:.8rem;font-weight:800}
    .count-pill small{color:var(--ts-text-subtle);font-size:.68rem}
    .count-pill.full{color:var(--ts-danger)}
    .primary-add{display:inline-flex;align-items:center;justify-content:center;gap:.45rem;height:38px;padding:0 .9rem;color:var(--ts-text-on-accent);border:0;border-radius:11px;background:linear-gradient(135deg,var(--ts-primary),var(--ts-indigo,#6366f1));box-shadow:0 8px 20px color-mix(in srgb,var(--ts-primary) 30%,transparent);font-size:.78rem;font-weight:800;cursor:pointer}
    .primary-add:disabled{opacity:.45;cursor:not-allowed}
    .doc-list{display:grid;gap:.45rem;margin:0;padding:0;list-style:none}
    .doc-item{display:flex;align-items:center;gap:.65rem;width:100%;min-height:58px!important;padding:.55rem .65rem;color:var(--ts-text);border:1px solid var(--ts-border);border-radius:12px;background:var(--ts-bg-elevated);text-align:left;cursor:pointer;transition:border-color var(--ts-transition),background var(--ts-transition),box-shadow var(--ts-transition)}
    .doc-item:hover{border-color:var(--ts-border-strong);background:var(--ts-surface-hover)}
    .doc-item.active{border-color:var(--ts-primary);background:color-mix(in srgb,var(--ts-primary) 10%,var(--ts-bg-elevated));box-shadow:0 0 0 3px color-mix(in srgb,var(--ts-primary) 18%,transparent)}
    .doc-item.is-new{border-style:dashed;cursor:default}
    .doc-icon{display:grid;place-items:center;width:36px;height:36px;flex:0 0 36px;color:var(--ts-primary);border-radius:10px;background:color-mix(in srgb,var(--ts-primary) 14%,transparent);font-size:1rem}
    .doc-icon.is-pdf{color:#e5484d;background:color-mix(in srgb,#e5484d 14%,transparent)}
    .doc-icon.is-image{color:#12a594;background:color-mix(in srgb,#12a594 14%,transparent)}
    .doc-icon.big{width:46px;height:46px;flex-basis:46px;border-radius:12px;font-size:1.3rem}
    .doc-text{display:grid;flex:1;min-width:0}
    .doc-text strong{overflow:hidden;font-size:.82rem;text-overflow:ellipsis;white-space:nowrap}
    .doc-text small{color:var(--ts-text-subtle);font-size:.68rem}
    .doc-state{padding:.15rem .5rem;color:var(--ts-text-subtle);border:1px solid var(--ts-border-strong);border-radius:999px;font-size:.6rem;font-weight:800;white-space:nowrap}
    .doc-state.on{color:var(--ts-success);border-color:color-mix(in srgb,var(--ts-success) 40%,transparent)}
    .list-tips{display:grid;gap:.5rem;margin:0;padding:.8rem 0 0;border-top:1px solid var(--ts-border);list-style:none}
    .list-tips li{display:flex;gap:.5rem;color:var(--ts-text-muted);font-size:.72rem;line-height:1.45}
    .list-tips i{color:var(--ts-primary)}
    .list-tips strong{color:var(--ts-text)}

    /* ---------- Editor ---------- */
    .legal-editor-card{position:relative;display:grid}
    .editor-body{display:grid;gap:1.1rem;padding:1.15rem 1.2rem 1.2rem}
    .editor-head{display:grid;gap:.55rem}
    .meta-chips{display:flex;flex-wrap:wrap;gap:.4rem}
    .chip{display:inline-flex;align-items:center;gap:.3rem;padding:.2rem .55rem;color:var(--ts-text-muted);border:1px solid var(--ts-border-strong);border-radius:999px;font-size:.68rem;font-weight:700}
    .field{display:grid;gap:.35rem;min-width:0;margin:0}
    .field>span{color:var(--ts-text-subtle);font-size:.66rem!important;font-weight:800!important;letter-spacing:.04em;text-transform:uppercase}
    .field .req{color:var(--ts-danger);font-style:normal}
    .field input[type=text]{box-sizing:border-box;width:100%;height:44px;min-height:44px!important;padding:0 .8rem!important;color:var(--ts-text);border:1px solid var(--ts-border-strong);border-radius:11px;background:var(--ts-surface);font-size:.95rem!important;font-weight:700;outline:none}
    .field input[type=text]:focus{border-color:var(--ts-primary);box-shadow:0 0 0 3px color-mix(in srgb,var(--ts-primary) 22%,transparent)}
    .field input.invalid{border-color:var(--ts-danger)}
    .field-hint{display:flex;gap:.35rem;color:var(--ts-text-subtle);font-size:.72rem;line-height:1.45}
    /* !important: .superadmin-container small fuerza el color atenuado. */
    .field-error{display:flex;align-items:center;gap:.35rem;color:var(--ts-danger)!important;font-size:.74rem;font-weight:700}
    .source-switch{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:.6rem}
    .source-switch button{display:flex;align-items:center;gap:.7rem;min-height:62px!important;padding:.65rem .8rem;color:var(--ts-text-muted);border:1px solid var(--ts-border-strong);border-radius:12px;background:var(--ts-surface);text-align:left;cursor:pointer;transition:border-color var(--ts-transition),background var(--ts-transition),color var(--ts-transition)}
    .source-switch button>i{display:grid;place-items:center;width:34px;height:34px;flex:0 0 34px;border-radius:10px;background:color-mix(in srgb,var(--ts-primary) 10%,transparent);font-size:1rem}
    .source-switch span{display:grid;gap:1px}
    .source-switch strong{color:var(--ts-text);font-size:.82rem}
    .source-switch em{font-size:.7rem;font-style:normal}
    .source-switch button.on{color:var(--ts-text);border-color:var(--ts-primary);background:color-mix(in srgb,var(--ts-primary) 10%,var(--ts-surface));box-shadow:0 0 0 3px color-mix(in srgb,var(--ts-primary) 16%,transparent)}
    .source-switch button.on>i{color:var(--ts-text-on-accent);background:var(--ts-primary)}
    .dropzone{display:grid;justify-items:center;gap:.4rem;padding:2rem 1rem;color:var(--ts-text-muted);border:2px dashed var(--ts-border-strong);border-radius:14px;background:color-mix(in srgb,var(--ts-primary) 4%,transparent);text-align:center;cursor:pointer;transition:border-color var(--ts-transition),background var(--ts-transition)}
    .dropzone:hover,.dropzone:focus-visible,.dropzone.over,.file-card.over{border-color:var(--ts-primary);background:color-mix(in srgb,var(--ts-primary) 10%,transparent);outline:none}
    .dropzone.invalid{border-color:var(--ts-danger)}
    .drop-icon{display:grid;place-items:center;width:52px;height:52px;color:var(--ts-primary);border-radius:16px;background:color-mix(in srgb,var(--ts-primary) 14%,transparent);font-size:1.5rem}
    .dropzone strong{color:var(--ts-text);font-size:.88rem}
    .dropzone small{font-size:.74rem}
    .file-card{display:flex;align-items:center;gap:.8rem;padding:.8rem .9rem;border:1px solid var(--ts-border-strong);border-radius:14px;background:var(--ts-surface);transition:border-color var(--ts-transition),background var(--ts-transition)}
    .file-text{display:grid;flex:1;min-width:0}
    .file-text strong{overflow:hidden;color:var(--ts-text);font-size:.86rem;text-overflow:ellipsis;white-space:nowrap}
    .file-text small{color:var(--ts-text-subtle);font-size:.72rem}
    .file-actions{display:flex;align-items:center;gap:.45rem}
    .file-actions .secondary-btn i{margin-right:.3rem}
    .icon-action{display:grid;place-items:center;width:38px;height:38px;min-height:38px!important;flex:0 0 38px;box-sizing:border-box;padding:0;border:1px solid var(--ts-border-strong);border-radius:10px;color:var(--ts-text-muted);background:transparent;cursor:pointer}
    .icon-action.danger{color:var(--ts-danger);border-color:color-mix(in srgb,var(--ts-danger) 40%,transparent);background:color-mix(in srgb,var(--ts-danger) 10%,transparent)}
    .icon-action.danger:hover{background:color-mix(in srgb,var(--ts-danger) 22%,transparent)}
    .icon-action:disabled{opacity:.4;cursor:not-allowed}
    .visibility-switch{display:inline-flex;align-items:center;gap:.8rem;width:100%;min-height:58px!important;padding:.65rem .85rem;color:var(--ts-text-subtle);border:1px solid var(--ts-border-strong);border-radius:12px;background:var(--ts-surface);text-align:left;cursor:pointer}
    .switch-track{position:relative;width:30px;height:18px;flex:0 0 30px;border-radius:999px;background:var(--ts-border-strong);transition:background var(--ts-transition)}
    .switch-knob{position:absolute;top:2px;left:2px;width:14px;height:14px;border-radius:50%;background:var(--ts-bg-gray-0);box-shadow:0 1px 3px var(--ts-shadow-gray-1000-a30);transition:transform 220ms cubic-bezier(.2,.8,.2,1)}
    .visibility-switch.on{color:var(--ts-success);border-color:color-mix(in srgb,var(--ts-success) 45%,transparent);background:color-mix(in srgb,var(--ts-success) 10%,transparent)}
    .visibility-switch.on .switch-track{background:var(--ts-success)}
    .visibility-switch.on .switch-knob{transform:translateX(12px)}
    .switch-text{display:grid;gap:1px}
    .switch-text strong{font-size:.82rem}
    .switch-text em{color:var(--ts-text-subtle);font-size:.72rem;font-style:normal;font-weight:600}

    /* ---------- Barra de guardado ---------- */
    .legal-actions{position:sticky;bottom:0;z-index:6;display:flex;align-items:center;justify-content:space-between;gap:.8rem;padding:.85rem 1.2rem;border-top:1px solid var(--ts-border);border-radius:0 0 16px 16px;background:var(--ts-bg-elevated)}
    .legal-actions.dirty{border-top-color:color-mix(in srgb,var(--ts-warning) 55%,transparent);box-shadow:0 -12px 26px var(--ts-shadow-gray-1000-a32),inset 0 2px 0 color-mix(in srgb,var(--ts-warning) 35%,transparent)}
    .save-state{display:inline-flex;align-items:center;gap:.45rem;color:var(--ts-text-subtle);font-size:.76rem;font-weight:700}
    .save-state i{color:var(--ts-success)}
    .save-state.pending{color:var(--ts-text)}
    .save-state.pending i{color:var(--ts-warning);font-size:.45rem;animation:pulse 1.6s ease-in-out infinite}
    .action-buttons{display:flex;align-items:center;gap:.55rem}
    .action-buttons :is(.action-btn,.secondary-btn) i{margin-right:.35rem}
    .spin{display:inline-block;animation:spin .8s linear infinite}

    /* ---------- Vista previa (mismo aspecto que el modal público) ---------- */
    .legal-preview{position:relative;display:flex;flex-direction:column;width:min(920px,100%);height:min(860px,calc(100dvh - 32px));overflow:hidden;color:var(--ts-text);border:1px solid var(--ts-border-strong);border-radius:20px;background:var(--ts-bg-elevated);box-shadow:var(--ts-shadow-floating);animation:modal-in .24s cubic-bezier(.2,.8,.2,1) both}
    .lp-head{display:flex;align-items:center;gap:.85rem;padding:1rem 1.15rem;border-bottom:1px solid var(--ts-border);background:linear-gradient(180deg,color-mix(in srgb,var(--ts-primary) 12%,transparent),transparent)}
    .lp-badge{display:grid;place-items:center;width:44px;height:44px;flex:0 0 44px;color:var(--ts-text-on-accent);border-radius:13px;background:linear-gradient(135deg,var(--ts-primary),var(--ts-indigo,#6366f1));font-size:1.2rem}
    .lp-heading{display:grid;flex:1;min-width:0}
    .lp-eyebrow{color:var(--ts-primary);font-size:.62rem;font-weight:800;letter-spacing:.1em;text-transform:uppercase}
    .lp-heading h3{margin:.1rem 0 0;overflow:hidden;color:var(--ts-text);font-size:1.15rem;font-weight:800;text-overflow:ellipsis;white-space:nowrap}
    .lp-heading small{color:var(--ts-text-subtle);font-size:.72rem}
    .lp-close{display:grid;place-items:center;width:40px;height:40px;min-height:40px!important;flex:0 0 40px;padding:0;color:var(--ts-text-muted);border:1px solid var(--ts-border);border-radius:11px;background:transparent;cursor:pointer}
    .lp-close:hover{color:var(--ts-text);background:var(--ts-surface-hover)}
    .lp-viewer{flex:1 1 auto;min-height:0}
    .lp-loading{position:absolute;inset:74px 0 0;display:flex;align-items:center;justify-content:center;gap:.6rem;color:var(--ts-text-muted);background:color-mix(in srgb,var(--ts-bg-elevated) 80%,transparent)}

    .legal-loading,.legal-load-error{display:flex;align-items:center;gap:.7rem;padding:2rem;color:var(--ts-text-muted);font-size:.9rem}
    .legal-load-error{flex-wrap:wrap;color:var(--ts-danger)}
    .legal-load-error .secondary-btn{margin-left:auto}
    .spinner{width:20px;height:20px;border:3px solid color-mix(in srgb,var(--ts-primary) 30%,transparent);border-top-color:var(--ts-primary);border-radius:50%;animation:spin .8s linear infinite}
    @keyframes spin{to{transform:rotate(360deg)}}
    @keyframes pulse{0%,100%{opacity:1}50%{opacity:.35}}
    @keyframes modal-in{from{opacity:0;transform:translateY(12px) scale(.98)}to{opacity:1;transform:none}}

    /* ---------- Responsive (según el ancho real del panel) ---------- */
    @container legal-shell (max-width: 900px){
      .legal-layout{grid-template-columns:minmax(0,1fr)}
      .legal-list-card{position:static}
      .doc-list{grid-auto-flow:column;grid-auto-columns:minmax(240px,300px);overflow-x:auto;padding-bottom:.3rem;scroll-snap-type:x mandatory}
      .doc-list li{scroll-snap-align:start}
      .list-tips{display:none}
    }
    @container legal-shell (max-width: 620px){
      .editor-body{padding:1rem .9rem}
      .legal-actions{flex-wrap:wrap;padding:.75rem .9rem}
      .action-buttons{width:100%;flex-wrap:wrap}
      .action-buttons .action-btn{flex:1 1 100%}
      .action-buttons .secondary-btn{flex:1}
      .doc-list{grid-auto-columns:82%}
      /* Estado debajo del título: en tarjetas angostas se encimaba con la fecha. */
      .doc-item{display:grid;grid-template-columns:36px minmax(0,1fr);row-gap:.3rem}
      .doc-state{grid-column:2;justify-self:start}
      .source-switch{grid-template-columns:1fr}
      .file-card{flex-wrap:wrap}
      .file-actions{width:100%;justify-content:flex-end}
    }
    @media (max-width:640px){
      .legal-preview{height:calc(100dvh - 16px);border-radius:16px}
      .lp-head{padding:.8rem .85rem}
      .lp-badge{display:none}
    }
  `],
})
export class PlatformLegalDocumentsAdminComponent implements OnInit {
  private readonly svc = inject(PlatformLegalDocumentsService);
  private readonly notifications = inject(NotificationService);
  private readonly dialogs = inject(DialogService);

  readonly loading = signal(true);
  readonly loadError = signal('');
  readonly saving = signal(false);
  readonly documents = signal<LegalDocument[]>([]);
  readonly limits = signal<LegalDocumentLimits>(DEFAULT_LIMITS);
  readonly titleError = signal('');
  readonly contentError = signal('');
  readonly fileError = signal('');
  readonly dragOver = signal(false);
  readonly previewOpen = signal(false);
  readonly previewBlob = signal<Blob | null>(null);
  readonly previewLoading = signal(false);
  /** Se incrementa con cada edición para recalcular `dirty`. */
  private readonly revision = signal(0);
  private readonly selectedId = signal<number | null | undefined>(undefined);

  draft: Draft = this.emptyDraft();

  readonly atLimit = computed(() => this.documents().length >= this.limits().documents);
  readonly hasSelection = computed(() => this.selectedId() !== undefined);
  readonly current = computed(() => {
    this.revision();
    const id = this.selectedId();
    return id ? this.documents().find(doc => doc.id === id) || null : null;
  });
  readonly dirty = computed(() => {
    this.revision();
    const doc = this.current();
    if (this.selectedId() === undefined) return false;
    if (!doc) return true; // documento nuevo sin crear
    return this.draft.title.trim() !== doc.title
      || this.draft.source !== doc.source
      || this.draft.is_published !== doc.is_published
      || !!this.draft.file
      || this.draft.remove_file
      || this.normalizeHtml(this.draft.content) !== this.normalizeHtml(doc.content);
  });
  readonly maxMb = computed(() => Math.round(this.limits().file_max_bytes / 1024 / 1024));
  readonly acceptAttr = computed(() => this.limits().file_extensions.map(ext => `.${ext}`).join(','));

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.loadError.set('');
    this.svc.load().subscribe({
      next: res => {
        this.documents.set(res.documents || []);
        this.limits.set({ ...DEFAULT_LIMITS, ...(res.limits || {}) });
        const keep = this.documents().find(doc => doc.id === this.selectedId()) || this.documents()[0];
        if (keep) this.applyDocument(keep); else this.selectedId.set(undefined);
        this.loading.set(false);
      },
      error: err => this.loadError.set(friendlyError(err, 'No se pudieron cargar los documentos legales.')),
    });
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.previewOpen()) this.closePreview();
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.dirty() && !this.saving()) event.preventDefault();
  }

  trackDoc(_: number, doc: LegalDocument): number {
    return doc.id;
  }

  touch(): void {
    this.titleError.set('');
    this.contentError.set('');
    this.revision.update(n => n + 1);
  }

  async select(doc: LegalDocument): Promise<void> {
    if (doc.id === this.draft.id) return;
    if (!(await this.confirmDiscard())) return;
    this.applyDocument(doc);
  }

  async startNew(): Promise<void> {
    if (this.atLimit() || !(await this.confirmDiscard())) return;
    this.selectedId.set(null);
    this.draft = this.emptyDraft();
    this.clearErrors();
    this.revision.update(n => n + 1);
  }

  setSource(source: LegalSource): void {
    if (this.draft.source === source) return;
    this.draft.source = source;
    this.fileError.set('');
    this.touch();
  }

  // ---------- Archivo ----------

  activeFileInfo(): { name: string; mime: string; size: number } | null {
    if (this.draft.file) return { name: this.draft.file.name, mime: this.mimeFor(this.draft.file), size: this.draft.file.size };
    if (this.draft.remove_file) return null;
    return this.current()?.file || null;
  }

  onFilePicked(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (file) this.acceptFile(file);
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.dragOver.set(true);
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.dragOver.set(false);
    const file = event.dataTransfer?.files?.[0];
    if (file) this.acceptFile(file);
  }

  removeFile(): void {
    if (this.draft.file) this.draft.file = null;
    else this.draft.remove_file = true;
    this.fileError.set('');
    this.touch();
  }

  private acceptFile(file: File): void {
    const extension = (file.name.split('.').pop() || '').toLowerCase();
    if (!this.limits().file_extensions.includes(extension)) {
      this.fileError.set('Formato no permitido. Sube un PDF o una imagen (PNG, JPG o WEBP).');
      return;
    }
    if (file.size > this.limits().file_max_bytes) {
      this.fileError.set(`El archivo pesa ${this.formatSize(file.size)}; el máximo es ${this.maxMb()} MB.`);
      return;
    }
    if (!file.size) {
      this.fileError.set('El archivo está vacío.');
      return;
    }
    this.draft.file = file;
    this.draft.remove_file = false;
    this.fileError.set('');
    this.touch();
  }

  private mimeFor(file: File): string {
    const extension = (file.name.split('.').pop() || '').toLowerCase();
    return MIME_BY_EXTENSION[extension] || file.type || '';
  }

  // ---------- Guardar / descartar / eliminar ----------

  async discard(): Promise<void> {
    const doc = this.current();
    if (doc) this.applyDocument(doc);
    else if (this.documents()[0]) this.applyDocument(this.documents()[0]);
    else { this.selectedId.set(undefined); this.draft = this.emptyDraft(); }
  }

  async save(): Promise<void> {
    if (!this.validate()) return;
    this.saving.set(true);
    const payload = {
      title: this.draft.title.trim(),
      source: this.draft.source,
      content: this.draft.content,
      is_published: this.draft.is_published,
      file: this.draft.file,
      remove_file: this.draft.remove_file,
    };
    const request = this.draft.id === null ? this.svc.create(payload) : this.svc.update(this.draft.id, payload);
    try {
      const res = await firstValueFrom(request);
      const saved = res.document;
      this.documents.update(list => {
        const exists = list.some(doc => doc.id === saved.id);
        return exists ? list.map(doc => (doc.id === saved.id ? saved : doc)) : [...list, saved];
      });
      this.applyDocument(saved);
      this.notifications.success(res.msg || 'Documento legal guardado.');
    } catch (err) {
      const message = friendlyError(err, 'No fue posible guardar el documento legal.');
      if (/archivo|formato|MB/i.test(message)) this.fileError.set(message);
      else if (/contenido/i.test(message)) this.contentError.set(message);
      else if (/título/i.test(message)) this.titleError.set(message);
      this.notifications.error(message);
    } finally {
      this.saving.set(false);
    }
  }

  async remove(): Promise<void> {
    const doc = this.current();
    if (!doc || doc.is_system) return;
    const confirmed = await this.dialogs.confirm({
      title: 'Eliminar documento legal',
      message: `«${doc.title}» dejará de mostrarse en la landing y en el sitio de clientes. Esta acción no se puede deshacer.`,
      confirmText: 'Eliminar',
      danger: true,
      tone: 'danger',
    });
    if (!confirmed) return;
    this.saving.set(true);
    try {
      const res = await firstValueFrom(this.svc.remove(doc.id));
      this.documents.update(list => list.filter(item => item.id !== doc.id));
      const next = this.documents()[0];
      if (next) this.applyDocument(next); else { this.selectedId.set(undefined); this.draft = this.emptyDraft(); }
      this.notifications.success(res.msg || 'Documento eliminado.');
    } catch (err) {
      this.notifications.error(friendlyError(err, 'No fue posible eliminar el documento.'));
    } finally {
      this.saving.set(false);
    }
  }

  private validate(): boolean {
    this.clearErrors();
    let ok = true;
    if (!this.draft.title.trim()) { this.titleError.set('Escribe el título del documento.'); ok = false; }
    if (this.draft.is_published && this.draft.source === 'editor' && !plainText(this.draft.content)) {
      this.contentError.set('Escribe el contenido o desmarca «Publicado» para guardarlo como borrador.');
      ok = false;
    }
    if (this.draft.is_published && this.draft.source === 'file' && !this.activeFileInfo()) {
      this.fileError.set('Sube el archivo o desmarca «Publicado» para guardarlo como borrador.');
      ok = false;
    }
    if (!ok) this.notifications.error('Revisa los campos marcados antes de guardar.');
    return ok;
  }

  // ---------- Vista previa ----------

  async openPreview(): Promise<void> {
    this.previewBlob.set(null);
    this.previewOpen.set(true);
    document.body.style.overflow = 'hidden';
    if (this.draft.source !== 'file') return;
    if (this.draft.file) { this.previewBlob.set(this.draft.file); return; }
    const doc = this.current();
    if (!doc?.file || this.draft.remove_file) return;
    this.previewLoading.set(true);
    try {
      this.previewBlob.set(await firstValueFrom(this.svc.downloadFile(doc.id)));
    } catch (err) {
      this.notifications.error(friendlyError(err, 'No fue posible cargar el archivo para la vista previa.'));
    } finally {
      this.previewLoading.set(false);
    }
  }

  closePreview(): void {
    this.previewOpen.set(false);
    this.previewBlob.set(null);
    document.body.style.overflow = '';
  }

  previewMeta(): string {
    const doc = this.current();
    const pending = this.dirty() ? ' · Cambios sin guardar' : '';
    if (!doc) return `Nuevo documento${pending}`;
    const date = formatLongDate(doc.updated_at);
    return `Versión ${doc.version} · Actualizado el ${date.charAt(0).toLowerCase()}${date.slice(1)}${pending}`;
  }

  // ---------- Utilidades de presentación ----------

  iconFor(source: LegalSource, mime?: string | null): string {
    if (source !== 'file') return 'bi-file-earmark-text';
    return mime === 'application/pdf' ? 'bi-file-earmark-pdf' : 'bi-file-earmark-image';
  }

  iconClass(source: LegalSource, mime?: string | null): string {
    if (source !== 'file') return '';
    return mime === 'application/pdf' ? 'is-pdf' : 'is-image';
  }

  fileTypeLabel(mime: string): string {
    return mime === 'application/pdf' ? 'PDF' : `Imagen ${mime.replace('image/', '').toUpperCase()}`;
  }

  formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  }

  shortDate(iso: string): string {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat('es-SV', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'America/El_Salvador' }).format(date);
  }

  private applyDocument(doc: LegalDocument): void {
    this.selectedId.set(doc.id);
    this.draft = { id: doc.id, title: doc.title, source: doc.source, content: doc.content, is_published: doc.is_published, file: null, remove_file: false };
    this.clearErrors();
    this.revision.update(n => n + 1);
  }

  private emptyDraft(): Draft {
    return { id: null, title: '', source: 'editor', content: '', is_published: true, file: null, remove_file: false };
  }

  private clearErrors(): void {
    this.titleError.set('');
    this.contentError.set('');
    this.fileError.set('');
  }

  private async confirmDiscard(): Promise<boolean> {
    if (!this.dirty()) return true;
    return this.dialogs.confirm({
      title: 'Descartar cambios',
      message: 'Tienes cambios sin guardar en este documento. ¿Quieres descartarlos?',
      confirmText: 'Descartar',
      tone: 'warning',
    });
  }

  /** El editor limpia el HTML al escribir; se compara ignorando espacios. */
  private normalizeHtml(html: string): string {
    return plainText(html) ? (html || '').replace(/\s+/g, ' ').trim() : '';
  }
}
